import { create } from "zustand";
import { send_request } from "../../utils/Request";
import {
  extractMessageAddedChatMessage,
  extractTaskQueueEvent,
  getSseEventPayload,
  getSseEventType,
  isTaskQueueSseEvent,
} from "../../utils/ChatEventReducer";
import useChatStreamStore from "./useChatStreamStore";
import usePublicChatStore from "./usePublicChatStore";
import useTaskQueueStore from "./useTaskQueueStore";
import config from "../../config";
import useProjectStore from "../ProjectCanvas/useProjectStore";
import {
  log as _log,
  critical as _crit,
  findIncompleteToolParts,
  summarizeIncompleteTools,
  summarizeMessages,
  summarizeParts,
} from "../../utils/ChatStreamLogger";
import { stopManagedRun as stopManagedRunRequest } from "./ManagedRunPolicy/managedRunApi";

function _getClientId() {
  return useProjectStore.getState().clientId;
}

const STATUS_WAITING_FOR_REVIEW = "WAITING_FOR_REVIEW";
const STATUS_SESSION_UNDER_REVIEW = "SESSION_UNDER_REVIEW";
const STATUS_IN_PROGRESS = "IN_PROGRESS";
const STATUS_WAITING_FOR_WORKERS = "WAITING_FOR_WORKERS";
const STATUS_WAITING_FOR_SCHEDULED_MESSAGE = "WAITING_FOR_SCHEDULED_MESSAGE";
const STATUS_WAITING_FOR_FORM_SUBMISSION = "WAITING_FOR_FORM_SUBMISSION";
const STATUS_CANCELLING = "CANCELLING";
const DEFAULT_BLOCKED_WHILE_REVIEW_MESSAGE =
  "This conversation is currently under review. Please wait for the reviewer to finish.";

function parseMaybeJsonPayload(payload) {
  if (!payload) return null;
  if (typeof payload === "string") {
    try {
      return JSON.parse(payload);
    } catch (_error) {
      return null;
    }
  }
  if (typeof payload === "object") {
    return payload;
  }
  return null;
}

function normalizeSessionStatusReason(value) {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function extractApiStatusCode(responseOrError) {
  if (!responseOrError || typeof responseOrError !== "object") {
    return 0;
  }
  return Number(
    responseOrError?.status || responseOrError?.response?.status || 0
  );
}

function extractApiPayload(responseOrError) {
  if (!responseOrError || typeof responseOrError !== "object") {
    return null;
  }
  const rawPayload = responseOrError?.data ?? responseOrError?.response?.data;
  return parseMaybeJsonPayload(rawPayload);
}

function resolveApiErrorMessage(responseOrError, fallbackMessage) {
  const payload = extractApiPayload(responseOrError);
  if (payload && typeof payload === "object") {
    if (typeof payload.error === "string" && payload.error.trim()) {
      return payload.error.trim();
    }
    if (typeof payload.message === "string" && payload.message.trim()) {
      return payload.message.trim();
    }
  }
  if (
    typeof responseOrError?.message === "string" &&
    responseOrError.message.trim()
  ) {
    return responseOrError.message.trim();
  }
  return fallbackMessage;
}

function inferMimeTypeFromFileName(fileName) {
  if (typeof fileName !== "string") return "text/plain";
  const lower = fileName.toLowerCase();
  if (lower.endsWith(".md")) return "text/markdown";
  if (lower.endsWith(".txt")) return "text/plain";
  if (lower.endsWith(".json")) return "application/json";
  if (lower.endsWith(".csv")) return "text/csv";
  return "text/plain";
}

function extractBlockedIngressPayload(responseOrError) {
  const statusCode = extractApiStatusCode(responseOrError);
  if (statusCode !== 409) {
    return null;
  }
  const payload = extractApiPayload(responseOrError);
  if (!payload || payload.status !== STATUS_SESSION_UNDER_REVIEW) {
    return null;
  }
  return {
    chatSessionId: String(payload.chatSessionId || ""),
    sessionStatus: String(payload.sessionStatus || STATUS_WAITING_FOR_REVIEW),
    message:
      typeof payload.message === "string" && payload.message.trim()
        ? payload.message.trim()
        : DEFAULT_BLOCKED_WHILE_REVIEW_MESSAGE,
  };
}

const _TERMINAL_WORKER_STATUSES = new Set(["COMPLETED", "CANCELLED", "ERROR"]);
const _STREAMABLE_SESSION_STATUSES = new Set([
  STATUS_IN_PROGRESS,
  STATUS_WAITING_FOR_WORKERS,
  STATUS_WAITING_FOR_SCHEDULED_MESSAGE,
  STATUS_WAITING_FOR_FORM_SUBMISSION,
  STATUS_WAITING_FOR_REVIEW,
  // 409 blocked-ingress flows (extractBlockedIngressPayload) produce SESSION_UNDER_REVIEW
  // as a non-terminal "under review" state; treat it as streamable so SSE reconnect/processing
  // continues until the session leaves review.
  STATUS_SESSION_UNDER_REVIEW,
]);

const _ACTIVE_RUN_EVENT_TYPES = new Set([
  "tool.invoked",
  "tool.completed",
  "tool.failed",
  "tool_activity",
  "worker_status_changed",
]);

function _isStreamableSessionStatus(status) {
  return !status || _STREAMABLE_SESSION_STATUSES.has(status);
}

function _isTerminalForStreaming(status) {
  return Boolean(status) && !_isStreamableSessionStatus(status) && status !== STATUS_CANCELLING;
}

function _isKnownStreamableSessionStatus(status) {
  return Boolean(status) && _isStreamableSessionStatus(status);
}

function _isActivelyRunningSessionStatus(status) {
  return status === STATUS_IN_PROGRESS;
}

function _markSubmittedStreamStarted(reason, getStore, setStore) {
  if (getStore().chatStatus !== 'submitted') return;
  _clearSubmittedRecovery();
  _markChatStatusReason(reason);
  setStore({ chatStatus: 'streaming' });
}

function _hasIncompleteWorkers(workerStatuses) {
  if (!workerStatuses) return false;
  return Object.values(workerStatuses).some(
    (w) => w?.status && !_TERMINAL_WORKER_STATUSES.has(w.status)
  );
}

let _optimisticIdCounter = 0;
let _streamUpdateRafId = null;
let _pendingStreamSessionId = null;
let _pendingStreamMessage = null;
let _latestFetchTarget = null;
let _submittedRecoveryTimer = null;

// Per-session timestamp (ms) of the most recent SSE session_complete event.
// Used to reject silent REST fetches that race backend persistence and would
// overwrite freshly streamed messages with stale, partial data.
const _recentSessionCompleteAtMs = new Map();
const POST_COMPLETE_REST_GUARD_MS = 5000;

// Tracks tool.invoked / tool.completed / tool.failed SSE events per session so
// we can detect tools that completed at the session level but never updated
// message parts (the "stuck spinner" bug).
const _toolLifecycleBySession = new Map();

function _recordToolLifecycleEvent(chatSessionId, eventType, payload) {
  const toolCallId = payload?.toolCallId || payload?.callId || null;
  const toolName = payload?.toolName || payload?.name || "?";
  if (!toolCallId) {
    _crit(
      "AgentStore",
      "warn",
      `tool lifecycle ${eventType} missing toolCallId session=${chatSessionId}`,
      payload
    );
    return;
  }

  let sessionMap = _toolLifecycleBySession.get(chatSessionId);
  if (!sessionMap) {
    sessionMap = new Map();
    _toolLifecycleBySession.set(chatSessionId, sessionMap);
  }

  const prev = sessionMap.get(toolCallId) || {
    toolName,
    invoked: false,
    completed: false,
    failed: false,
  };
  if (eventType === "tool.invoked") prev.invoked = true;
  if (eventType === "tool.completed") prev.completed = true;
  if (eventType === "tool.failed") prev.failed = true;
  prev.toolName = toolName;
  prev.lastEvent = eventType;
  prev.lastAt = Date.now();
  sessionMap.set(toolCallId, prev);

  _crit(
    "AgentStore",
    "info",
    `tool lifecycle ${eventType} session=${chatSessionId} tool=${toolName} callId=${toolCallId} invoked=${prev.invoked} completed=${prev.completed} failed=${prev.failed}`
  );
}

function _auditStuckTools(getStore, chatSessionId, context) {
  const detail = getStore().sessionDetail;
  if (!detail || detail.chatSessionId !== chatSessionId) return;

  const chatStatus = getStore().chatStatus;
  const sessionStatus = detail.status || "?";
  const incomplete = findIncompleteToolParts(detail.messages);
  const sessionMap = _toolLifecycleBySession.get(chatSessionId);
  const invokedNotCompleted = [];

  if (sessionMap) {
    for (const [callId, record] of sessionMap) {
      if (record.invoked && !record.completed && !record.failed) {
        invokedNotCompleted.push({
          toolCallId: callId,
          toolName: record.toolName,
          lastEvent: record.lastEvent,
        });
      }
    }
  }

  const streamingMsgs = (detail.messages || []).filter(
    (m) => m.status === "streaming"
  );
  const sessionTerminal = _isTerminalForStreaming(sessionStatus);
  const chatIdle = chatStatus === "ready";
  const hasStuck =
    incomplete.length > 0
    || invokedNotCompleted.length > 0
    || (chatIdle && streamingMsgs.length > 0)
    || (sessionTerminal && chatIdle && incomplete.length > 0);

  if (!hasStuck) return;

  const lifecycleSummary = sessionMap
    ? [...sessionMap.entries()].map(
        ([callId, record]) =>
          `${record.toolName}(${callId}):inv=${record.invoked ? 1 : 0}:done=${record.completed ? 1 : 0}:fail=${record.failed ? 1 : 0}`
      )
    : [];

  _crit(
    "AgentStore",
    "warn",
    `STUCK TOOLS [${context}] session=${chatSessionId} chatStatus=${chatStatus} sessionStatus=${sessionStatus} incompleteParts=${incomplete.length} invokedNotCompleted=${invokedNotCompleted.length} streamingMsgs=${streamingMsgs.length} incomplete=[${summarizeIncompleteTools(detail.messages)}]`,
    {
      incompleteParts: incomplete,
      invokedNotCompleted,
      streamingMessageIds: streamingMsgs.map((m) => m.messageId || m.id),
      toolLifecycle: lifecycleSummary,
      messages: summarizeMessages(detail.messages),
    }
  );
}

function _clearToolLifecycle(chatSessionId) {
  _toolLifecycleBySession.delete(chatSessionId);
}

function _markRecentSessionComplete(chatSessionId) {
  const completedAt = Date.now();
  _recentSessionCompleteAtMs.set(chatSessionId, completedAt);
  setTimeout(() => {
    if (_recentSessionCompleteAtMs.get(chatSessionId) === completedAt) {
      _recentSessionCompleteAtMs.delete(chatSessionId);
    }
  }, POST_COMPLETE_REST_GUARD_MS);
}

let _chatStatusReason = null;
function _markChatStatusReason(reason) {
  _chatStatusReason = reason;
}

function _clearSubmittedRecovery() {
  if (_submittedRecoveryTimer) {
    clearTimeout(_submittedRecoveryTimer);
    _submittedRecoveryTimer = null;
  }
}

function _scheduleSubmittedRecovery(chatSessionId, getStore) {
  _clearSubmittedRecovery();
  _submittedRecoveryTimer = setTimeout(() => {
    _submittedRecoveryTimer = null;
    const cs = getStore().chatStatus;
    if (cs === 'submitted') {
      const streamState = useChatStreamStore.getState().sessionStreamStateBySessionId[chatSessionId];
      _crit(
        "AgentStore",
        "error",
        `STUCK on 'submitted' for 8s — no stream activity. session=${chatSessionId} streamState=${streamState || 'idle'}. Falling back to REST fetch.`
      );
      getStore().fetchSessionDetail(chatSessionId, { silent: true });
    }
  }, 8000);
}

function _isEmptyAssistantStreamMessage(msg) {
  if (!msg || msg.role === "user" || msg.role === "system") return false;
  const content = typeof msg.content === "string" ? msg.content.trim() : "";
  const hasParts = Array.isArray(msg.parts) && msg.parts.length > 0;
  const hasToolCalls = Array.isArray(msg.toolCalls) && msg.toolCalls.length > 0;
  return !content && !hasParts && !hasToolCalls;
}

function _generateOptimisticId() {
  return `_optimistic_${Date.now()}_${++_optimisticIdCounter}`;
}
function _getLocalUserDisplayName() {
  const { userDisplayName, userName } = useProjectStore.getState();
  return userDisplayName || userName || undefined;
}


const useAgentStore = create((set, get) => ({
  agents: [],
  loading: false,
  error: null,
  sendError: null,
  sessions: [],
  toolCatalog: [],
  sessionsLoading: false,
  sessionsHasMore: true,
  sessionsLoadingMore: false,
  sessionDetail: null,
  sessionDetailLoading: false,
  chatStatus: 'ready',
  _isPublicSession: false,
  _workerStatuses: {},
  /** Map<agentId, boolean> — true if agent has any running or externally wakeable session */
  agentHasActiveSession: {},
  clearError: () => { _markChatStatusReason('clearError'); set({ chatStatus: 'ready', sendError: null }); },

  /**
   * Bootstrap agent activity with a single bulk fetch via recent-sessions.
   */
  bootstrapAgentActivity: async () => {
    const clientId = _getClientId();
    if (!clientId) return;
    const response = await send_request(
      "ai-service/ai/agent/recent-sessions",
      "",
      { clientId, limit: 50 },
      "GET"
    );
    if (response instanceof Error || !response?.data) return;
    const sessions = Array.isArray(response.data) ? response.data : [];
    const activeMap = {};
    for (const s of sessions) {
      if (s.agentId && _isStreamableSessionStatus(s.status)) {
        activeMap[s.agentId] = true;
      }
    }
    set({ agentHasActiveSession: activeMap });
  },

  /**
   * Handle an SSE activity event from the agent channel.
   * Updates agentHasActiveSession based on session lifecycle events.
   */
  handleAgentActivityEvent: (agentId, eventType, payload) => {
    if (eventType === "new_session") {
      console.debug("[AgentActivity] new_session →", agentId, "active=true");
      set((s) => ({
        agentHasActiveSession: { ...s.agentHasActiveSession, [agentId]: true },
      }));
      return;
    }

    // session_activity carries wrapped payload from SSE:
    // { streamVersion, type, sessionId, payload: { originalEventType, status, ... } }
    const inner = payload?.payload ?? payload;
    const originalType = inner?.originalEventType;
    const newStatus = inner?.status;
    console.debug("[AgentActivity] event →", agentId, { eventType, originalType, newStatus, inner });

    if (originalType === "status_changed" && typeof newStatus === "string") {
      const active = _isStreamableSessionStatus(newStatus);
      console.debug("[AgentActivity] status_changed →", agentId, "active=", active);
      set((s) => ({
        agentHasActiveSession: {
          ...s.agentHasActiveSession,
          [agentId]: active,
        },
      }));
      return;
    }

    // session_complete — mark inactive. If another session on this agent is still
    // running, its own status_changed/new_session events will re-activate.
    if (originalType === "session_complete") {
      console.debug("[AgentActivity] session_complete →", agentId, "active=false");
      set((s) => ({
        agentHasActiveSession: { ...s.agentHasActiveSession, [agentId]: false },
      }));
    }
  },

  fetchAgents: async () => {
    set({ loading: true, error: null });
    try {
      const response = await send_request(
        "project-service/agent/list",
        "",
        {},
        "GET"
      );
      set({ agents: response?.data || [], loading: false });
    } catch (e) {
      set({ error: e.message || "Failed to load agents", loading: false });
    }
  },

  /** Silently refresh agents list without triggering loading skeleton. */
  refreshAgents: async () => {
    try {
      const response = await send_request(
        "project-service/agent/list",
        "",
        {},
        "GET"
      );
      set({ agents: response?.data || [] });
    } catch (_e) {
      // silent — keeps existing list on failure
    }
  },

  createAgent: async (agentData) => {
    try {
      const response = await send_request(
        "project-service/agent/create",
        agentData,
        {},
        "POST"
      );
      if (response?.data) {
        set((s) => ({ agents: [...s.agents, response.data] }));
        return response.data;
      }
      const serverMessage = response?.response?.data?.message;
      const errorId = response?.response?.data?.errorId;
      const status = response?.response?.status;
      const errorMsg = serverMessage
        ? `${serverMessage}${errorId ? ` (Error ID: ${errorId})` : ""}`
        : status
          ? `Server returned ${status}. Please try again.`
          : response?.message || "Failed to create agent";
      set({ error: errorMsg });
      return null;
    } catch (e) {
      const serverMessage = e?.response?.data?.message;
      const errorId = e?.response?.data?.errorId;
      const errorMsg = serverMessage
        ? `${serverMessage}${errorId ? ` (Error ID: ${errorId})` : ""}`
        : e.message || "Failed to create agent";
      set({ error: errorMsg });
      return null;
    }
  },

  updateAgent: async (agentId, agentData) => {
    try {
      const response = await send_request(
        `project-service/agent/${agentId}`,
        agentData,
        {},
        "PUT"
      );
      if (response?.data) {
        set((s) => {
          const agents = [...s.agents];
          const index = agents.findIndex((a) => a.agentId === agentId);
          if (index >= 0) agents[index] = response.data;
          return { agents };
        });
        return response.data;
      }
      // send_request returns the error instead of throwing — extract server message
      const serverMessage = response?.response?.data?.message;
      const errorId = response?.response?.data?.errorId;
      const status = response?.response?.status;
      const errorMsg = serverMessage
        ? `${serverMessage}${errorId ? ` (Error ID: ${errorId})` : ""}`
        : status
          ? `Server returned ${status}. Please try again.`
          : response?.message || "Failed to update agent";
      set({ error: errorMsg });
      return null;
    } catch (e) {
      const serverMessage = e?.response?.data?.message;
      const errorId = e?.response?.data?.errorId;
      const errorMsg = serverMessage
        ? `${serverMessage}${errorId ? ` (Error ID: ${errorId})` : ""}`
        : e.message || "Failed to update agent";
      set({ error: errorMsg });
      return null;
    }
  },

  fetchAgent: async (agentId) => {
    try {
      const response = await send_request(
        `project-service/agent/${agentId}`,
        "",
        {},
        "GET"
      );
      return response?.data || null;
    } catch (e) {
      set({ error: e.message || "Failed to load agent" });
      return null;
    }
  },

  fetchSessions: async (agentId, { silent = false, limit = 25 } = {}) => {
    if (!silent) set({ sessionsLoading: true });
    try {
      const query = limit > 0 ? { limit, offset: 0 } : {};
      const response = await send_request(
        `project-service/agent/${agentId}/sessions`,
        "",
        query,
        "GET"
      );
      const data = response?.data || [];
      const sessions = Array.isArray(data) ? data : [];
      set({
        sessions,
        sessionsHasMore: limit > 0 && sessions.length >= limit,
      });
    } catch (e) {
      set({
        error: e.message || "Failed to load sessions",
        sessions: [],
        sessionsHasMore: false,
      });
    }
    if (!silent) set({ sessionsLoading: false });
  },

  fetchMoreSessions: async (agentId, { limit = 25 } = {}) => {
    const { sessionsLoadingMore, sessionsHasMore, sessions } = get();
    if (sessionsLoadingMore || !sessionsHasMore) return;
    set({ sessionsLoadingMore: true });
    try {
      const offset = sessions.length;
      const response = await send_request(
        `project-service/agent/${agentId}/sessions`,
        "",
        { limit, offset },
        "GET"
      );
      const data = response?.data || [];
      const moreSessions = Array.isArray(data) ? data : [];
      const existingIds = new Set(sessions.map((s) => s.chatSessionId));
      const dedupedMore = moreSessions.filter((s) => !existingIds.has(s.chatSessionId));
      set({
        sessions: [...sessions, ...dedupedMore],
        sessionsHasMore: moreSessions.length >= limit,
        sessionsLoadingMore: false,
      });
    } catch (_e) {
      set({ sessionsLoadingMore: false });
    }
  },

  fetchToolCatalog: async () => {
    try {
      const clientId = _getClientId();
      const response = await send_request(
        "project-service/agent/tools/catalog",
        "",
        clientId ? { clientId } : {},
        "GET"
      );
      const catalog = response?.data;
      const toolCatalog = Array.isArray(catalog) ? catalog : [];
      set({ toolCatalog });
      return toolCatalog;
    } catch (e) {
      set({
        error: e.message || "Failed to load tool catalog",
        toolCatalog: [],
      });
      return [];
    }
  },

  fetchWorkflowOptions: async () => {
    try {
      const response = await send_request(
        "project-service/agent/workflow-options",
        "",
        {},
        "GET"
      );
      const options = response?.data;
      return Array.isArray(options) ? options : [];
    } catch (e) {
      set({ error: e.message || "Failed to load workflow options" });
      return [];
    }
  },

  // Fallback data loader — only used when SSE is unavailable (poll fallback,
  // submitted-recovery timeout). SSE snapshot is the primary data source.
  fetchSessionDetail: async (chatSessionId, { silent = false } = {}) => {
    const streamState = useChatStreamStore.getState().sessionStreamStateBySessionId[chatSessionId];
    const chatStatus = get().chatStatus;
    if (
      silent
      && streamState === 'connected'
      && (chatStatus === 'submitted' || chatStatus === 'streaming')
    ) {
      return;
    }

    // Reject silent REST fetches that fire shortly after an SSE session_complete.
    // Backend persistence races the SSE event by hundreds of milliseconds, and
    // the REST response routinely returns the just-completed assistant message
    // with only its EARLIEST parts (e.g. only the first tool call) - when
    // _replaceMessages then runs it overwrites the live, fully streamed message
    // and the UI snaps back mid-conversation (the "stuck on get_table" bug).
    // SSE has already delivered the authoritative final state; nothing useful
    // can come from a REST fetch in this window.
    const lastCompleteMs = _recentSessionCompleteAtMs.get(chatSessionId);
    if (silent && lastCompleteMs && (Date.now() - lastCompleteMs) < POST_COMPLETE_REST_GUARD_MS) {
      return;
    }

    _crit(
      "AgentStore",
      "info",
      `fetchSessionDetail session=${chatSessionId} silent=${silent} currentChatStatus=${chatStatus}`
    );
    if (!silent) set({ sessionDetailLoading: true });
    _latestFetchTarget = chatSessionId;
    try {
      const response = await send_request(
        `project-service/agent/session/${chatSessionId}`,
        "",
        {},
        "GET"
      );
      const detail = response?.data || null;
      if (detail && !detail.chatSessionId && !detail.messages) {
        if (!silent) set({ sessionDetailLoading: false });
        return;
      }

      if (_latestFetchTarget !== chatSessionId) return;

      // Backend race: the SSE session_complete event is emitted before the DB
      // status flips to a terminal value, so a fetch right after completion
      // can return stale IN_PROGRESS. If our local view is already terminal,
      // treat the response as stale and preserve local status — otherwise we
      // overwrite sessionDetail.status and regress chatStatus ready→streaming
      // with no SSE to recover, leaving the UI stuck forever.
      const localStatus = get().sessionDetail?.chatSessionId === chatSessionId
        ? get().sessionDetail?.status
        : null;
      const localTerminal = _isTerminalForStreaming(localStatus);
      const effectiveDetail = (localTerminal && detail?.status === STATUS_IN_PROGRESS)
        ? { ...detail, status: localStatus }
        : detail;

      get()._replaceMessages(chatSessionId, effectiveDetail?.messages, effectiveDetail);

      if (
        effectiveDetail?.status
        && !_isStreamableSessionStatus(effectiveDetail.status)
      ) {
        _auditStuckTools(
          get,
          chatSessionId,
          `fetchSessionDetail:status=${effectiveDetail.status}`
        );
      }

      // Only update chatStatus if SSE is NOT connected for this session —
      // when SSE is connected, it is the authoritative source for chatStatus.
      const streamState = useChatStreamStore.getState().sessionStreamStateBySessionId[chatSessionId];
      const sseActive = streamState === 'connected' || streamState === 'connecting';
      if (!sseActive) {
        const cs = get().chatStatus;
        if (effectiveDetail?.status === STATUS_IN_PROGRESS) {
          if (cs !== 'streaming') {
            _markChatStatusReason(`fetchSessionDetail(poll):backend=IN_PROGRESS,sseState=${streamState || 'idle'}`);
            set({ chatStatus: 'streaming' });
          }
        } else if (effectiveDetail?.status && effectiveDetail.status !== STATUS_IN_PROGRESS) {
          if (cs === 'submitted' || cs === 'streaming' || cs === 'error') {
            _markChatStatusReason(`fetchSessionDetail(poll):backend=${effectiveDetail.status}`);
            set({ chatStatus: 'ready' });
          }
        }
      }
    } catch (e) {
      set((s) => ({
        error: e.message || "Failed to load session detail",
        sessionDetail: s.sessionDetail || null,
      }));
    }
    // Always clear sessionDetailLoading when this is the latest fetch target,
    // even for silent fetches — SSE-first flow sets sessionDetailLoading: true
    // and relies on the fallback fetch to clear it if SSE fails to connect.
    if (_latestFetchTarget === chatSessionId) {
      set({ sessionDetailLoading: false });
    } else if (!silent) {
      set({ sessionDetailLoading: false });
    }
  },

  disconnectStream: (options) => {
    useChatStreamStore.getState().disconnectStream(options);
  },

  disconnectAgentStream: () => {
    useChatStreamStore.getState().disconnectAgentStream();
  },

  // ── Message primitives ─────────────────────────────────────
  // Every mutation to sessionDetail.messages goes through one of
  // these three functions. Nothing else touches the array.
  // _upsertMessage handles optimistic→server reconciliation via clientRequestId.

  _replaceMessages: (chatSessionId, serverMessages, serverDetail) => {
    const currentId = get().sessionDetail?.chatSessionId;
    const targetSessionId = serverDetail?.chatSessionId ?? chatSessionId ?? null;
    const isSwitchingSession = targetSessionId !== (currentId ?? null);
    if (isSwitchingSession) {
      set({ _workerStatuses: {} });
    }
    set((s) => {
      const incoming = [...(serverMessages || [])];

      // Only preserve local-only messages (optimistic, streaming assistant)
      // when staying on the SAME session. When switching sessions, the old
      // session's messages must not leak into the new one.
      if (!isSwitchingSession) {
        const currentMsgs = s.sessionDetail?.messages || [];
        const incomingIds = new Set();
        for (const m of incoming) {
          if (m.messageId) incomingIds.add(m.messageId);
          if (m.id) incomingIds.add(m.id);
        }

        const localOnly = currentMsgs.filter((m) => {
          if (m._optimistic) return true;
          const mid = m.messageId || m.id;
          if (mid && !incomingIds.has(mid) && m.role === "assistant") return true;
          return false;
        });

        if (localOnly.length > 0) {
          const nonDup = localOnly.filter((m) => {
            if (m._optimistic && m.clientRequestId && incoming.some((im) => im.clientRequestId === m.clientRequestId)) {
              return false;
            }
            if (m._optimistic && incoming.some((im) => im.role === m.role && im.content === m.content)) {
              return false;
            }
            // Drop non-optimistic assistant messages whose content already
            // exists in the server payload (SSE message_added can deliver a
            // message with a different ID than the snapshot/fetch response).
            if (!m._optimistic && m.role === "assistant") {
              const mParts = JSON.stringify(m.parts || []);
              if (incoming.some((im) => im.role === "assistant" && JSON.stringify(im.parts || []) === mParts)) {
                return false;
              }
              if (m.content && incoming.some((im) => im.role === "assistant" && im.content === m.content)) {
                return false;
              }
            }
            return true;
          });
          if (nonDup.length > 0) incoming.push(...nonDup);
        }
      }

      const detail = serverDetail
        ? { ...serverDetail, messages: incoming }
        : s.sessionDetail
          ? { ...s.sessionDetail, chatSessionId, messages: incoming }
          : { chatSessionId, messages: incoming, status: "IN_PROGRESS" };

      return { sessionDetail: detail };
    });
    const replacedMessages =
      get().sessionDetail?.chatSessionId === targetSessionId
        ? get().sessionDetail?.messages
        : null;
    const incompleteIncoming = findIncompleteToolParts(replacedMessages);
    if (incompleteIncoming.length > 0) {
      _crit(
        "AgentStore",
        "warn",
        `_replaceMessages incomplete tool parts session=${targetSessionId || chatSessionId || "?"} count=${incompleteIncoming.length} [${summarizeIncompleteTools(replacedMessages)}]`,
        { incompleteParts: incompleteIncoming }
      );
    }
  },

  _upsertMessage: (chatSessionId, msg) => {
    set((s) => {
      const detail = s.sessionDetail;
      if (!detail || detail.chatSessionId !== chatSessionId) {
        return {};
      }
      if (!msg) return {};

      const id = msg.messageId || msg.id;
      const before = detail.messages || [];

      if (id) {
        const idx = before.findIndex(
          (m) => (m.messageId && m.messageId === id) || (m.id && m.id === id)
        );
        if (idx !== -1) {
          const msgs = [...before];
          msgs[idx] = msg;
          return { sessionDetail: { ...detail, messages: msgs } };
        }
      }

      if (msg.clientRequestId || (msg.role === "user" && !msg._optimistic)) {
        const optIdx = msg.clientRequestId
          ? before.findIndex((m) => m._optimistic && m.clientRequestId === msg.clientRequestId)
          : before.findIndex((m) => m._optimistic && m.role === "user" && m.content === msg.content);
        if (optIdx !== -1) {
          const msgs = [...before];
          msgs[optIdx] = msg;
          return { sessionDetail: { ...detail, messages: msgs } };
        }
      }

      // Content-based dedup for assistant messages — SSE message_added replay
      // can deliver messages with different IDs than the snapshot/fetch response.
      // Uses indexed Maps to avoid repeated JSON.stringify during streaming.
      if (msg.role === "assistant") {
        const hasParts = Array.isArray(msg.parts) && msg.parts.length > 0;
        if (hasParts || msg.content) {
          const partsIndex = new Map();
          const contentIndex = new Map();
          before.forEach((m, idx) => {
            if (m.role !== "assistant") return;
            if (Array.isArray(m.parts) && m.parts.length > 0) {
              const sig = JSON.stringify(m.parts);
              if (!partsIndex.has(sig)) partsIndex.set(sig, idx);
              return;
            }
            if (m.content && !contentIndex.has(m.content)) {
              contentIndex.set(m.content, idx);
            }
          });
          const dupIdx = hasParts
            ? partsIndex.get(JSON.stringify(msg.parts))
            : contentIndex.get(msg.content);
          if (dupIdx !== undefined) {
            const msgs = [...before];
            msgs[dupIdx] = msg;
            return { sessionDetail: { ...detail, messages: msgs } };
          }
        }
      }

      return { sessionDetail: { ...detail, messages: [...before, msg] } };
    });
  },

  _removeMessage: (chatSessionId, messageId) => {
    set((s) => {
      const detail = s.sessionDetail;
      if (!detail || detail.chatSessionId !== chatSessionId) return {};
      const msgs = (detail.messages || []).filter((m) => m.messageId !== messageId);
      return { sessionDetail: { ...detail, messages: msgs } };
    });
  },

  // ── End message primitives ────────────────────────────────

  _processUiChunk: (eventName, data, sessionId) => {
    _clearSubmittedRecovery();
    const result = useChatStreamStore.getState().handleUiChunk(eventName, data, sessionId);
    if (!result) return;
    if (result._allMessages) {
      for (const msg of result._allMessages) {
        if (_isEmptyAssistantStreamMessage(msg)) {
          continue;
        }
        get()._upsertMessage(sessionId, { ...msg, role: "assistant", messageId: msg.id });
      }
    } else if (!_isEmptyAssistantStreamMessage(result)) {
      get()._upsertMessage(sessionId, { ...result, role: "assistant", messageId: result.id });
    }
  },

  subscribeToAgent: async (agentId) => {
    await useChatStreamStore.getState().subscribeToAgent(agentId, (aid) => {
      get().fetchSessions(aid, { silent: true });
    });
  },

  /**
   * Subscribe to session SSE events.
   * For private sessions: subscribeToSession(chatSessionId)
   * For public sessions:  subscribeToSession(chatSessionId, { publicSessionToken: "pct-..." })
   */
  subscribeToSession: async (chatSessionId, options = {}) => {
    const isPublic = !!options?.publicSessionToken;
    const connectId = isPublic ? options.publicSessionToken : chatSessionId;
    set({ _isPublicSession: isPublic });
    const upsertStream = (sessionId, streamMsg) => {
      if (!streamMsg || !streamMsg.id) return;
      if (_isEmptyAssistantStreamMessage(streamMsg)) {
        return;
      }
      get()._upsertMessage(sessionId, { ...streamMsg, role: "assistant", messageId: streamMsg.id });
    };

    const result = await useChatStreamStore.getState().subscribeToSession(
      connectId,
      chatSessionId,
      {
        reconnectWhileSubscribed: options?.reconnectWhileSubscribed === true,
        forceReconnect: options?.forceReconnect === true,
        skipAuth: isPublic,
        onEvent: (eventName, data, sessionId) => {
          const eventType = getSseEventType(eventName, data);
          if (eventType === "ui_chunk") {
            // Skip ui_chunk replay when session is already terminal —
            // the snapshot already contains the final messages and replaying
            // chunks creates duplicates with different streaming IDs.
            const detailStatus = get().sessionDetail?.status;
            if (_isTerminalForStreaming(detailStatus)) {
              return;
            }
            const envelope = data?.payload || data;
            const chunkType = envelope?.chunk?.type;
            const chunkResult = useChatStreamStore.getState().handleUiChunk(eventName, data, sessionId);
            const isFinish = chunkType === "finish";
            if (isFinish) {
              if (_streamUpdateRafId) {
                cancelAnimationFrame(_streamUpdateRafId);
                _streamUpdateRafId = null;
              }
              _pendingStreamMessage = null;
              _pendingStreamSessionId = null;
              const allMsgs = chunkResult?._allMessages;
              const sessionStatus = get().sessionDetail?.status;
              const isCancelling = sessionStatus === "CANCELLING";
              if (isCancelling) {
                // no-op
              } else if (allMsgs) {
                for (const msg of allMsgs) {
                  upsertStream(sessionId, msg);
                }
              } else if (chunkResult) {
                upsertStream(sessionId, chunkResult);
              }
            } else if (chunkResult) {
              if (get().chatStatus === 'submitted') {
                _clearSubmittedRecovery();
                _markChatStatusReason('sseChunk:firstChunkReceived');
                set({ chatStatus: 'streaming' });
              }
              if (_isEmptyAssistantStreamMessage(chunkResult)) {
                return;
              }
              if (_pendingStreamMessage && _pendingStreamMessage.id !== chunkResult.id) {
                upsertStream(_pendingStreamSessionId, _pendingStreamMessage);
              }
              _pendingStreamSessionId = sessionId;
              _pendingStreamMessage = chunkResult;
              if (!_streamUpdateRafId) {
                _streamUpdateRafId = requestAnimationFrame(() => {
                  _streamUpdateRafId = null;
                  if (_pendingStreamSessionId && _pendingStreamMessage) {
                    upsertStream(_pendingStreamSessionId, _pendingStreamMessage);
                  }
                });
              }
            }
            return;
          }
          get()._handleSseEvent(eventName, data, sessionId);
        },
        shouldReconnect: () => {
          const status = get().sessionDetail?.status;
          const hasWorkers = _hasIncompleteWorkers(get()._workerStatuses);
          return _isStreamableSessionStatus(status) || hasWorkers;
        },
        onPollFallback: (sid) => {
          get().fetchSessionDetail(sid, { silent: true });
        },
        pollIntervalMs: 3000,
        isSessionActive: () => {
          const s = get().sessionDetail;
          const cs = get().chatStatus;
          const hasWorkers = _hasIncompleteWorkers(get()._workerStatuses);
          return _isStreamableSessionStatus(s?.status) || cs !== 'ready' || hasWorkers;
        },
        isSessionTerminal: () => {
          const d = get().sessionDetail;
          const cs = get().chatStatus;
          const hasWorkers = _hasIncompleteWorkers(get()._workerStatuses);
          if (!d || d.chatSessionId !== chatSessionId) return false;
          if (_isStreamableSessionStatus(d.status)) return false;
          if (cs !== 'ready') return false;
          return !hasWorkers;
        },
      }
    );
    if (result?.aborted) {
      return;
    }
    const currentStatus = get().chatStatus;
    if (currentStatus === 'streaming' || currentStatus === 'error') {
      _markChatStatusReason(`streamClosed:connectionFailed=${!!result?.connectionFailed}`);
      set({ chatStatus: 'ready' });
      _auditStuckTools(
        get,
        chatSessionId,
        `streamClosed:connectionFailed=${!!result?.connectionFailed}`
      );
    }
    if (result?.connectionFailed) {
      get().fetchSessionDetail(chatSessionId, { silent: true });
    }
  },

  _handleSseEvent: (eventName, data, chatSessionId) => {
    const type = getSseEventType(eventName, data);
    const payload = getSseEventPayload(data);
    const streamState = useChatStreamStore.getState().sessionStreamStateBySessionId[chatSessionId];
    _crit(
      "AgentStore",
      "info",
      `_handleSseEvent type=${type} session=${chatSessionId} sseState=${streamState || 'idle'} chatStatus=${get().chatStatus}`
    );
    if (_ACTIVE_RUN_EVENT_TYPES.has(type)) {
      _markSubmittedStreamStarted(`sseActivity:${type}`, get, set);
    }
    if (
      type === "tool.invoked"
      || type === "tool.completed"
      || type === "tool.failed"
    ) {
      _recordToolLifecycleEvent(chatSessionId, type, payload);
    }
    if (isTaskQueueSseEvent(type)) {
      const queueEvent = extractTaskQueueEvent(eventName, data);
      if (queueEvent && chatSessionId) {
        useTaskQueueStore.getState().setTaskQueueEvent(chatSessionId, queueEvent);
      }
    }

    if (type === "snapshot") {
      if (!payload) {
        set({ sessionDetailLoading: false });
        return;
      }
      const currentDetail = get().sessionDetail;
      const currentChatStatus = get().chatStatus;
      const isSameSession = currentDetail?.chatSessionId === chatSessionId;
      const isTerminalSnapshot = _isTerminalForStreaming(payload.status);
      if (
        isSameSession
        && isTerminalSnapshot
        && (currentChatStatus === 'submitted' || currentChatStatus === 'streaming')
      ) {
        set({ sessionDetailLoading: false });
        return;
      }
      get()._replaceMessages(chatSessionId, payload.messages, payload);

      // SSE snapshot is the authoritative initial state.
      if (_isActivelyRunningSessionStatus(payload.status)) {
        _clearSubmittedRecovery();
        _markChatStatusReason(`sseSnapshot:backend=${payload.status}`);
        set({ sessionDetailLoading: false, chatStatus: 'streaming' });
      } else if (_isKnownStreamableSessionStatus(payload.status)) {
        const cs = get().chatStatus;
        _clearSubmittedRecovery();
        if (cs === 'submitted' || cs === 'streaming' || cs === 'error') {
          _markChatStatusReason(`sseSnapshot:backend=${payload.status}`);
          set({ sessionDetailLoading: false, chatStatus: 'ready' });
        } else {
          set({ sessionDetailLoading: false });
        }
      } else if (payload.status && payload.status !== STATUS_IN_PROGRESS) {
        const cs = get().chatStatus;
        if (cs === 'streaming' || cs === 'error') {
          _markChatStatusReason(`sseSnapshot:backend=${payload.status}`);
          set({ sessionDetailLoading: false, chatStatus: 'ready' });
        } else {
          set({ sessionDetailLoading: false });
        }
      } else {
        set({ sessionDetailLoading: false });
      }
    } else if (type === "message_added") {
      const msg = extractMessageAddedChatMessage(eventName, data);
      if (!msg) {
        return;
      }
      if (msg.role === "assistant") {
        let compId = null;
        const parts = msg.parts || [];
        for (const part of parts) {
          if (part.type === "tool" && part.input) {
            const inp = typeof part.input === "string" ? (() => { try { return JSON.parse(part.input); } catch (_e) { return null; } })() : part.input;
            if (inp?.componentId) { compId = inp.componentId; break; }
          }
        }
        if (!compId && msg.toolCall) {
          const toolArgs = msg.toolCall.arguments;
          if (typeof toolArgs === "string") {
            try { compId = JSON.parse(toolArgs)?.componentId; } catch (_e) { /* ignore */ }
          } else if (toolArgs && typeof toolArgs === "object") {
            compId = toolArgs.componentId;
          }
        }
        if (compId) {
          const handler = get()._canvasUpdateHandler;
          if (handler) handler({ action: '_tool_activity_start', componentId: compId });
        }
      }

      const normalized = { ...msg, messageId: msg.messageId || msg.id };

      if (get().chatStatus === 'submitted') {
        _markChatStatusReason('sseMessageAdded:firstMessage');
        set({ chatStatus: 'streaming' });
      }

      const currentMsgs = get().sessionDetail?.messages || [];
      const id = normalized.messageId;

      if (msg.role === "user") {
        if (id) {
          const idExists = currentMsgs.some(
            (m) => (m.messageId && m.messageId === id) || (m.id && m.id === id)
          );
          if (idExists) {
            return;
          }
        }
        const contentExists = currentMsgs.some(
          (m) => !m._optimistic && m.role === "user" && m.content === msg.content
        );
        if (contentExists) {
          return;
        }
      }

      if (_isEmptyAssistantStreamMessage(normalized)) {
        return;
      }

      get()._upsertMessage(chatSessionId, normalized);

      if (msg.role === "assistant") {
        const toolParts = (normalized.parts || []).filter((p) => p.type === "tool");
        if (toolParts.length > 0) {
          _crit(
            "AgentStore",
            "info",
            `message_added assistant tools session=${chatSessionId} msgId=${normalized.messageId || normalized.id} msgStatus=${normalized.status || "?"} parts={${summarizeParts(normalized.parts)}}`
          );
          _auditStuckTools(get, chatSessionId, "message_added");
        }
      }
    } else if (type === "status_changed") {
      const nextStatus = typeof payload === "string" ? payload : payload?.status;
      if (_isActivelyRunningSessionStatus(nextStatus)) {
        _clearSubmittedRecovery();
        _markChatStatusReason(`sseStatusChanged:${nextStatus}`);
        set({ chatStatus: 'streaming' });
      } else if (_isKnownStreamableSessionStatus(nextStatus)) {
        const cs = get().chatStatus;
        _clearSubmittedRecovery();
        if (cs === 'streaming' || cs === 'submitted' || cs === 'error') {
          _markChatStatusReason(`sseStatusChanged:${nextStatus}`);
          set({ chatStatus: 'ready' });
        }
      } else if (nextStatus && nextStatus !== STATUS_IN_PROGRESS) {
        const cs = get().chatStatus;
        if (cs === 'streaming' || cs === 'submitted' || cs === 'error') {
          _markChatStatusReason(`sseStatusChanged:${nextStatus}`);
          set({ chatStatus: 'ready' });
        }
      }
      set((s) => {
        if (
          s.sessionDetail &&
          s.sessionDetail.chatSessionId === chatSessionId
        ) {
          return {
            sessionDetail: {
              ...s.sessionDetail,
              status: nextStatus,
              statusReason:
                nextStatus === "ERROR"
                  ? s.sessionDetail.statusReason || null
                  : null,
            },
          };
        }
        return {};
      });
      if (get()._isPublicSession && nextStatus !== "IN_PROGRESS") {
        const cb = get()._publicSessionChangeCallback;
        if (cb) cb();
      }
      if (nextStatus && !_isStreamableSessionStatus(nextStatus)) {
        _auditStuckTools(get, chatSessionId, `status_changed:${nextStatus}`);
      }
    } else if (type === "session_complete") {
      _markRecentSessionComplete(chatSessionId);
      _clearSubmittedRecovery();
      if (_streamUpdateRafId) {
        cancelAnimationFrame(_streamUpdateRafId);
        _streamUpdateRafId = null;
      }

      const streamMsgs = useChatStreamStore.getState().getStreamMessages(chatSessionId);
      if (streamMsgs.length > 0) {
        for (const msg of streamMsgs) {
          if (msg && msg.id) {
            if (_isEmptyAssistantStreamMessage(msg)) {
              continue;
            }
            get()._upsertMessage(chatSessionId, { ...msg, role: msg.role || "assistant", messageId: msg.id });
          }
        }
      } else if (_pendingStreamMessage && _pendingStreamSessionId) {
        if (!_isEmptyAssistantStreamMessage(_pendingStreamMessage)) {
          get()._upsertMessage(_pendingStreamSessionId, {
            ..._pendingStreamMessage,
            role: "assistant",
            messageId: _pendingStreamMessage.id,
          });
        }
      }

      _pendingStreamMessage = null;
      _pendingStreamSessionId = null;
      useChatStreamStore.getState().resetChunkCounters();
      useChatStreamStore.getState().clearStreamState(chatSessionId);
      _markChatStatusReason('sseSessionComplete');
      set((s) => {
        const updates = {
          chatStatus: 'ready',
        };
        if (
          s.sessionDetail &&
          s.sessionDetail.chatSessionId === chatSessionId
        ) {
          const completion = payload || {};
          const newStatus = completion.status || "COMPLETED";
          const newIterations =
            completion.iterationsUsed ?? s.sessionDetail.iterationsUsed;
          const completionReason = normalizeSessionStatusReason(
            completion.reason || completion.error
          );
          const currentReason = normalizeSessionStatusReason(
            s.sessionDetail.statusReason
          );
          const nextStatusReason =
            completionReason || (newStatus === "ERROR" ? currentReason : null);
          updates.sessionDetail = {
            ...s.sessionDetail,
            status: newStatus,
            iterationsUsed: newIterations,
            statusReason: nextStatusReason,
          };
        }
        return updates;
      });
      _auditStuckTools(get, chatSessionId, "session_complete");
      _clearToolLifecycle(chatSessionId);
      if (!get()._isPublicSession) {
        get().refreshSessionInList(chatSessionId);
        // Intentionally no fetchSessionDetail here. session_complete is the
        // authoritative end-of-stream signal — sessionDetail.status,
        // iterationsUsed, statusReason were just written from the payload above,
        // and all messages arrived via SSE. A REST fetch here races backend
        // persistence and the response (which is missing the just-streamed
        // messages) overwrites the live SSE state via _replaceMessages.
      } else {
        const cb = get()._publicSessionChangeCallback;
        if (cb) cb();
      }
    } else if (type === "worker_status_changed") {
      if (payload?.workerSessionId) {
        set((s) => ({
          _workerStatuses: {
            ...s._workerStatuses,
            [payload.workerSessionId]: payload,
          },
        }));
      }
    } else if (type === "canvas_update") {
      const handler = get()._canvasUpdateHandler;
      if (handler) {
        handler(payload);
      }
    } else if (type === "tool_activity") {
      if (payload?.componentId) {
        const handler = get()._canvasUpdateHandler;
        if (handler) handler({ action: '_tool_activity_start', componentId: payload.componentId });
      }
    }
  },

  _canvasUpdateHandler: null,
  setCanvasUpdateHandler: (fn) => {
    set({ _canvasUpdateHandler: fn });
  },

  _publicSessionChangeCallback: null,
  setPublicSessionChangeCallback: (fn) => set({ _publicSessionChangeCallback: fn }),

  startChat: async (
    agentId,
    message,
    imageUrls,
    {
      projectId,
      projectName,
      tableId,
      tableName,
      artifactDefinitionId,
      artifactInstanceId,
      artifactComponentId,
      artifactTitle,
    } = {}
  ) => {
    const optimisticId = _generateOptimisticId();
    const clientRequestId = crypto.randomUUID();
    const senderDisplayName = _getLocalUserDisplayName();
    const optimisticMsg = {
      messageId: optimisticId,
      role: "user",
      content: message,
      timestamp: new Date().toISOString(),
      imageUrls: imageUrls || undefined,
      senderDisplayName,
      _optimistic: true,
      clientRequestId,
    };
    _clearSubmittedRecovery();
    _markChatStatusReason('startChat');
    set({ chatStatus: 'submitted', sendError: null });
    get()._replaceMessages(null, [optimisticMsg], {
      chatSessionId: null,
      messages: [optimisticMsg],
      status: "IN_PROGRESS",
    });
    try {
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const payload = { message, userTimezone, clientRequestId };
      if (imageUrls && imageUrls.length > 0) payload.imageUrls = imageUrls;
      if (projectId) payload.projectId = projectId;
      if (projectName) payload.projectName = projectName;
      if (tableId) payload.tableId = tableId;
      if (tableName) payload.tableName = tableName;
      if (artifactDefinitionId) payload.artifactDefinitionId = artifactDefinitionId;
      if (artifactInstanceId) payload.artifactInstanceId = artifactInstanceId;
      if (artifactComponentId) payload.artifactComponentId = artifactComponentId;
      if (artifactTitle) payload.artifactTitle = artifactTitle;
      const response = await send_request(
        `project-service/agent/${agentId}/chat`,
        payload,
        {},
        "POST"
      );
      const data = response?.data;
      if (data?.chatSessionId) {
        set((s) => ({
          sessionDetail: {
            ...s.sessionDetail,
            chatSessionId: data.chatSessionId,
            status: "IN_PROGRESS",
          },
        }));
        get().subscribeToSession(data.chatSessionId, { reconnectWhileSubscribed: true, forceReconnect: true });
        _scheduleSubmittedRecovery(data.chatSessionId, get);
        get().fetchSessions(agentId, { silent: true });
      }
      return data;
    } catch (e) {
      _clearSubmittedRecovery();
      _markChatStatusReason('startChat:error');
      set({ sendError: e.message || "Failed to start chat", chatStatus: 'error', sessionDetail: null });
      return null;
    }
  },

  sendMessage: async (chatSessionId, agentId, message, imageUrls) => {
    _log("Send", `sendMessage: session=${chatSessionId} chatStatus=${get().chatStatus} subscribedSession=${useChatStreamStore.getState().getSubscribedSessionId()}`);
    const optimisticId = _generateOptimisticId();
    const clientRequestId = crypto.randomUUID();
    const senderDisplayName = _getLocalUserDisplayName();
    _clearSubmittedRecovery();
    _markChatStatusReason('sendMessage');
    set({ chatStatus: 'submitted', sendError: null });
    get()._upsertMessage(chatSessionId, {
      messageId: optimisticId,
      role: "user",
      content: message,
      timestamp: new Date().toISOString(),
      imageUrls: imageUrls || undefined,
      senderDisplayName,
      _optimistic: true,
      clientRequestId,
    });
    try {
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const payload = { agentId, message, userTimezone, clientRequestId };
      if (imageUrls && imageUrls.length > 0) {
        payload.imageUrls = imageUrls;
      }
      const response = await send_request(
        `project-service/agent/session/${chatSessionId}/message`,
        payload,
        {},
        "POST"
      );
      const blockedPayload = extractBlockedIngressPayload(response);
      if (blockedPayload) {
        get().disconnectStream();
        get()._removeMessage(chatSessionId, optimisticId);
        _markChatStatusReason('sendMessage:blockedIngress');
        set((s) => {
          const detail = s.sessionDetail?.chatSessionId === chatSessionId
            ? s.sessionDetail
            : { chatSessionId, messages: [] };
          return {
            chatStatus: 'ready',
            error: null,
            sessionDetail: {
              ...detail,
              status: blockedPayload.sessionStatus,
              blockedWhileReviewMessage: blockedPayload.message,
              statusReason: null,
            },
          };
        });
        get().refreshSessionInList(chatSessionId);
        return { chatSessionId, blocked: true };
      }
      const statusCode = extractApiStatusCode(response);
      if (statusCode >= 400) {
        throw new Error(
          resolveApiErrorMessage(response, "Failed to send message")
        );
      }
      get().subscribeToSession(chatSessionId, { reconnectWhileSubscribed: true });
      _scheduleSubmittedRecovery(chatSessionId, get);
      return { chatSessionId };
    } catch (e) {
      _clearSubmittedRecovery();
      get().disconnectStream();
      const errorMsg = resolveApiErrorMessage(e, "Failed to send message");
      get()._removeMessage(chatSessionId, optimisticId);
      _markChatStatusReason('sendMessage:error');
      set({ sendError: errorMsg, chatStatus: 'error' });
      return null;
    }
  },

  sendPublicMessage: async (chatSessionId, publicSessionToken, agentId, message, imageUrls, senderDisplayName) => {
    const optimisticId = _generateOptimisticId();
    const clientRequestId = crypto.randomUUID();
    _clearSubmittedRecovery();
    _markChatStatusReason('sendPublicMessage');
    set({ chatStatus: 'submitted', sendError: null });
    get()._upsertMessage(chatSessionId, {
      messageId: optimisticId,
      role: "user",
      content: message,
      timestamp: new Date().toISOString(),
      imageUrls: imageUrls || undefined,
      senderDisplayName,
      _optimistic: true,
      clientRequestId,
    });
    try {
      const payload = { agentId, message, clientRequestId };
      if (imageUrls && imageUrls.length > 0) payload.imageUrls = imageUrls;
      await usePublicChatStore.getState().sendPublicChatMessage(publicSessionToken, payload);
      get().subscribeToSession(chatSessionId, { publicSessionToken, reconnectWhileSubscribed: true, forceReconnect: true });
      _scheduleSubmittedRecovery(chatSessionId, get);
      return { chatSessionId };
    } catch (e) {
      _clearSubmittedRecovery();
      get()._removeMessage(chatSessionId, optimisticId);
      const blockedPayload = extractBlockedIngressPayload(e);
      if (blockedPayload) {
        _markChatStatusReason('sendPublicMessage:blockedIngress');
        set((s) => {
          const detail = s.sessionDetail?.chatSessionId === chatSessionId
            ? s.sessionDetail
            : { chatSessionId, messages: [] };
          return {
            chatStatus: 'ready',
            sessionDetail: {
              ...detail,
              status: blockedPayload.sessionStatus,
              blockedWhileReviewMessage: blockedPayload.message,
              statusReason: null,
            },
          };
        });
        return { chatSessionId, blocked: true };
      }
      _markChatStatusReason('sendPublicMessage:error');
      set({ chatStatus: 'error', sendError: resolveApiErrorMessage(e, "Failed to send message") });
      return null;
    }
  },

  startHumanTakeover: async (chatSessionId) => {
    try {
      const response = await send_request(
        `project-service/agent/session/${chatSessionId}/human-takeover/start`,
        {},
        {},
        "POST"
      );
      const data = response?.data || null;
      set((s) => {
        if (s.sessionDetail?.chatSessionId === chatSessionId) {
          return {
            sessionDetail: {
              ...s.sessionDetail,
              status: data?.status || "HUMAN_TAKEOVER",
              statusReason: null,
            },
          };
        }
        return {};
      });
      await get().fetchSessionDetail(chatSessionId, { silent: true });
      get().refreshSessionInList(chatSessionId);
      return data;
    } catch (e) {
      const blockedPayload = extractBlockedIngressPayload(e);
      if (blockedPayload) {
        set((s) => {
          const existingDetail =
            s.sessionDetail?.chatSessionId === chatSessionId
              ? s.sessionDetail
              : { chatSessionId, messages: [] };
          return {
            sessionDetail: {
              ...existingDetail,
              status: blockedPayload.sessionStatus,
              blockedWhileReviewMessage: blockedPayload.message,
              statusReason: null,
            },
          };
        });
        get().refreshSessionInList(chatSessionId);
        return { blocked: true, ...blockedPayload };
      }
      set({
        error: resolveApiErrorMessage(e, "Failed to start human takeover"),
      });
      return null;
    }
  },

  endHumanTakeover: async (chatSessionId) => {
    try {
      const response = await send_request(
        `project-service/agent/session/${chatSessionId}/human-takeover/end`,
        {},
        {},
        "POST"
      );
      const data = response?.data || null;
      set((s) => {
        if (s.sessionDetail?.chatSessionId === chatSessionId) {
          return {
            sessionDetail: {
              ...s.sessionDetail,
              status: data?.status || "COMPLETED",
              statusReason: null,
            },
          };
        }
        return {};
      });
      await get().fetchSessionDetail(chatSessionId, { silent: true });
      get().refreshSessionInList(chatSessionId);
      return data;
    } catch (e) {
      const blockedPayload = extractBlockedIngressPayload(e);
      if (blockedPayload) {
        set((s) => {
          const existingDetail =
            s.sessionDetail?.chatSessionId === chatSessionId
              ? s.sessionDetail
              : { chatSessionId, messages: [] };
          return {
            sessionDetail: {
              ...existingDetail,
              status: blockedPayload.sessionStatus,
              blockedWhileReviewMessage: blockedPayload.message,
              statusReason: null,
            },
          };
        });
        get().refreshSessionInList(chatSessionId);
        return { blocked: true, ...blockedPayload };
      }
      set({
        error: resolveApiErrorMessage(e, "Failed to end human takeover"),
      });
      return null;
    }
  },

  cancelSession: async (chatSessionId) => {
    set((s) => {
      if (s.sessionDetail?.chatSessionId === chatSessionId) {
        return { sessionDetail: { ...s.sessionDetail, status: "CANCELLING" } };
      }
      return {};
    });
    try {
      await send_request(
        `project-service/agent/session/${chatSessionId}/cancel`,
        {},
        {},
        "POST"
      );
      _markChatStatusReason('cancelSession');
      set({ chatStatus: 'ready' });
    } catch (e) {
      set({ error: e.message || "Failed to cancel session" });
    }
  },

  stopManagedRun: async (chatSessionId, reason = "Stopped by user") => {
    try {
      const response = await stopManagedRunRequest(chatSessionId, reason);
      const payload = response?.data || {};
      set((s) => {
        if (s.sessionDetail?.chatSessionId !== chatSessionId) return {};
        return {
          sessionDetail: {
            ...s.sessionDetail,
            managedRunStatus: payload.managedRunStatus || "STOPPED",
          },
        };
      });
      get().refreshSessionInList(chatSessionId);
      return payload;
    } catch (e) {
      set({ error: resolveApiErrorMessage(e, "Failed to stop managed run") });
      return null;
    }
  },

  refreshSessionInList: (chatSessionId) => {
    set((s) => {
      const detail = s.sessionDetail;
      if (!detail || detail.chatSessionId !== chatSessionId) return {};
      const idx = s.sessions.findIndex(
        (sess) => sess.chatSessionId === chatSessionId
      );
      if (idx < 0) return {};
      const sessions = [...s.sessions];
      sessions[idx] = {
        ...sessions[idx],
        status: detail.status,
        statusReason: detail.statusReason || null,
        iterationsUsed: detail.iterationsUsed,
        title: detail.title || sessions[idx].title,
        updatedAt: detail.updatedAt || sessions[idx].updatedAt,
      };
      return { sessions };
    });
  },

  deleteAgent: async (agentId) => {
    try {
      await send_request(`project-service/agent/${agentId}`, "", {}, "DELETE");
      set((s) => ({
        agents: s.agents.filter((a) => a.agentId !== agentId),
      }));
      return true;
    } catch (e) {
      set({ error: e.message || "Failed to delete agent" });
      return false;
    }
  },

  saveEditedAgentFile: async ({
    agentId,
    previousFileId,
    fileName,
    content,
    mimeType,
  }) => {
    const normalizedAgentId = String(agentId || "").trim();
    const normalizedFileName = String(fileName || "").trim() || "document.txt";
    const normalizedMimeType =
      String(mimeType || "").trim() ||
      inferMimeTypeFromFileName(normalizedFileName);

    if (!normalizedAgentId) {
      const message = "Missing agent ID for file save";
      set({ error: message });
      throw new Error(message);
    }

    const formData = new FormData();
    formData.append(
      "file",
      new Blob([content ?? ""], { type: normalizedMimeType }),
      normalizedFileName
    );
    formData.append("configId", "fileupload");
    formData.append("type", "upload");
    formData.append("projectId", "");
    formData.append("projectSessionId", "");
    formData.append("formSessionId", "");
    formData.append("agentId", normalizedAgentId);

    try {
      const uploadResponse = await send_request(
        "project-service/project/file-library/create",
        formData,
        "",
        "POST",
        { "content-type": "multipart/form-data" }
      );

      const uploaded = uploadResponse?.data || null;
      const uploadedFileId = String(uploaded?.id || "").trim();
      if (!uploadedFileId) {
        const message = resolveApiErrorMessage(
          uploadResponse,
          "Failed to save edited file"
        );
        set({ error: message });
        throw new Error(message);
      }

      let warning = "";
      const normalizedPreviousId = String(previousFileId || "").trim();
      if (normalizedPreviousId && normalizedPreviousId !== uploadedFileId) {
        const deleteResponse = await send_request(
          `project-service/project/file-library/agent/${normalizedAgentId}/deletes`,
          { deleteList: [normalizedPreviousId] },
          "",
          "POST"
        );

        const deleteFailed = extractApiStatusCode(deleteResponse) >= 400;
        if (deleteFailed) {
          warning = resolveApiErrorMessage(
            deleteResponse,
            "Updated file saved, but old file deletion failed"
          );
        }
      }

      return {
        fileId: uploadedFileId,
        fileName: uploaded?.fileName || normalizedFileName,
        downloadUrl: `${config.FILE_SERVICE.QUERY_URL}/${uploadedFileId}`,
        mimeType:
          uploaded?.mimeType || uploaded?.fileType || normalizedMimeType,
        size: uploaded?.fileSize ?? null,
        createdAt: uploaded?.createDate || null,
        agentId: normalizedAgentId,
        warning,
      };
    } catch (e) {
      const message = e?.message || "Failed to save edited file";
      set({ error: message });
      throw e instanceof Error ? e : new Error(message);
    }
  },

  uploadChatAttachment: async (file, agentId) => {
    const normalizedAgentId = String(agentId || "").trim();
    if (!normalizedAgentId) throw new Error("Missing agent ID for file upload");

    const formData = new FormData();
    formData.append("file", file, file.name);
    formData.append("configId", "fileupload");
    formData.append("type", "upload");
    formData.append("projectId", "");
    formData.append("projectSessionId", "");
    formData.append("formSessionId", "");
    formData.append("agentId", normalizedAgentId);

    const uploadResponse = await send_request(
      "project-service/project/file-library/create",
      formData,
      "",
      "POST",
      { "content-type": "multipart/form-data" }
    );

    const uploaded = uploadResponse?.data || null;
    const uploadedFileId = String(uploaded?.id || "").trim();
    if (!uploadedFileId) {
      throw new Error("Failed to upload file");
    }

    return {
      fileId: uploadedFileId,
      fileName: uploaded?.fileName || file.name,
      downloadUrl: `${config.FILE_SERVICE.QUERY_URL}/${uploadedFileId}`,
      mimeType: uploaded?.mimeType || uploaded?.fileType || file.type,
      size: uploaded?.fileSize ?? file.size,
    };
  },

  deleteChatAttachment: async (fileId, agentId) => {
    const normalizedAgentId = String(agentId || "").trim();
    const normalizedFileId = String(fileId || "").trim();
    if (!normalizedAgentId || !normalizedFileId) return;

    await send_request(
      `project-service/project/file-library/agent/${normalizedAgentId}/deletes`,
      { deleteList: [normalizedFileId] },
      "",
      "POST"
    );
  },
}));

// Always-on logging of chatStatus transitions. Key signal for diagnosing
// "chat stuck on in_progress" — tells you exactly when (and if) the UI
// moved submitted → streaming → ready, or got stuck.
//
// Guarded behind a window-scoped flag so HMR / double module evaluation
// doesn't register duplicate subscribers (which would emit each transition
// log N times).
const _CHAT_STATUS_SUBSCRIPTION_KEY = '__useAgentStoreChatStatusSubscription__';
if (typeof window !== 'undefined' && !window[_CHAT_STATUS_SUBSCRIPTION_KEY]) {
  let _prevChatStatus = useAgentStore.getState().chatStatus;
  window[_CHAT_STATUS_SUBSCRIPTION_KEY] = useAgentStore.subscribe((state) => {
    const next = state.chatStatus;
    if (next !== _prevChatStatus) {
      const level = next === 'error' ? 'error' : 'info';
      const reason = _chatStatusReason || 'unknown';
      _crit("AgentStore", level, `chatStatus ${_prevChatStatus} → ${next} (reason=${reason})`);

      if (next === 'streaming') {
        const streamState = useChatStreamStore.getState().sessionStreamStateBySessionId;
        const sid = state.sessionDetail?.chatSessionId;
        const cs = sid ? streamState[sid] : null;
        if (cs !== 'connected' && cs !== 'connecting') {
          _crit(
            "AgentStore",
            "error",
            `PHANTOM STREAMING — chatStatus=streaming but SSE is ${cs || 'idle'} for session=${sid}. reason=${reason}`
          );
        }
      }

      if (next === 'ready') {
        const sid = state.sessionDetail?.chatSessionId;
        if (sid) {
          _auditStuckTools(() => useAgentStore.getState(), sid, `chatStatus→ready:${reason}`);
        }
      }

      _prevChatStatus = next;
      _chatStatusReason = null;
    }
  });
}

export default useAgentStore;
